/**
 * Curates the 21-flag Permission enum into what a person deciding "who
 * should get this role" actually needs to read.
 *
 * Deliberately NOT a second, hand-maintained list of "what each role can
 * do" -- every capability below is a live lookup against `permissions` from
 * ./role, the same table `usePermissions()` reads. If a role's permissions
 * change, this view changes with it; it cannot drift into describing a role
 * that no longer exists in code.
 */

import { Permission, UserRole, permissions, roleOptions } from './role';

export interface RoleCapabilityItem {
  key: string;
  label: string;
  permission: Permission;
}

/**
 * Real actions, shown as a fixed checklist on every role card so two roles
 * can be compared at a glance -- a role that lacks one renders it muted
 * rather than omitting the row, which would make cards different lengths
 * and hide the "no" as an absence of information rather than an answer.
 *
 * CAN_SEE_JOB_CANDIDATE_MATCH_BUTTON and CAN_SEE_CANDIDATE_ACTION_COLUMN are
 * left out on purpose, not by oversight: the match button is granted to no
 * role today, and the action column is granted to every role, so neither
 * one distinguishes anything a reader comparing roles would care about.
 */
export const CAN_DO_ITEMS: RoleCapabilityItem[] = [
  {
    key: 'postJobs',
    label: 'Post jobs',
    permission: Permission.CAN_ADD_NEW_JOB,
  },
  {
    key: 'assignHiringManagers',
    label: 'Assign hiring managers to jobs',
    permission: Permission.CAN_SEE_JOB_ASSIGN_HM_CARD,
  },
  {
    key: 'sendCandidateEmails',
    label: 'Send candidate invitations and emails',
    permission: Permission.CAN_SEE_CANDIDATE_SEND_EMAIL_BUTTON,
  },
  {
    key: 'advanceStage',
    label: "Advance a candidate's stage",
    permission: Permission.CAN_SEE_CANDIDATE_NEXT_STAGE_BUTTON,
  },
  {
    key: 'rejectCandidate',
    label: 'Reject a candidate',
    permission: Permission.CAN_SEE_CANDIDATE_REJECT_BUTTON,
  },
  {
    key: 'addFeedback',
    label: 'Leave hiring-manager feedback',
    permission: Permission.CAN_SEE_CANDIDATE_ADD_FEEDBACK_BUTTON,
  },
  {
    key: 'recordOffer',
    label: 'Record an offer',
    permission: Permission.CAN_RECORD_OFFER_EVENT,
  },
  {
    key: 'viewFeedback',
    label: 'View hiring-manager feedback',
    permission: Permission.CAN_SEE_CANDIDATE_VIEW_HM_FEEDBACK_BUTTON,
  },
  {
    // CAN_SEE_USER_ACTIONS_COLUMN (edit/reset/delete a user) is folded into
    // this one item rather than listed separately: today it is granted to
    // exactly the same roles as CAN_ADD_NEW_USER, so it is the "manage"
    // half of the same capability, not a second one.
    key: 'manageTeam',
    label: 'Add and manage team members',
    permission: Permission.CAN_ADD_NEW_USER,
  },
];

export interface RoleCanDoResult extends RoleCapabilityItem {
  granted: boolean;
}

export const roleCanDo = (role: UserRole): RoleCanDoResult[] =>
  CAN_DO_ITEMS.map((item) => ({
    ...item,
    granted: permissions[role].includes(item.permission),
  }));

/** Extra, lower-stakes visibility -- named columns on the jobs table. */
const JOB_COLUMN_VISIBILITY: Array<{ permission: Permission; noun: string }> = [
  {
    permission: Permission.CAN_SEE_JOB_HIRING_MANAGER_COLUMN,
    noun: 'hiring-manager',
  },
  {
    permission: Permission.CAN_SEE_JOB_RECRUITER_COLUMN,
    noun: 'recruiter',
  },
  { permission: Permission.CAN_SEE_JOB_COMPANY_COLUMN, noun: 'company' },
  {
    permission: Permission.CAN_SEE_JOB_CUSTOMER_NAME_COLUMN,
    noun: 'customer',
  },
];

/**
 * Cross-customer visibility a role can also hold, beyond the jobs-table
 * columns above: a customer field on the post-job and add-user forms, and a
 * customer column on the users table. Folded into the same "Can see"
 * sentence rather than three more bullets -- it is the identical low-stakes
 * visibility tier, just surfacing on different screens. Today only Super
 * Admin holds any of these, so this only ever extends that one card's line.
 */
const EXTRA_VISIBILITY_PERMISSIONS = [
  Permission.CAN_SEE_JOB_POST_CUSTOMER_FIELD,
  Permission.CAN_SEE_ADD_USER_CUSTOMER_FIELD,
  Permission.CAN_SEE_USER_CUSTOMER_COLUMN,
];

const joinWithAnd = (items: string[]): string => {
  if (items.length <= 1) return items.join('');
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

/** One plain-language sentence for a role's extra visibility, or null if it has none. */
export const roleSeesLine = (role: UserRole): string | null => {
  const nouns = JOB_COLUMN_VISIBILITY.filter((v) =>
    permissions[role].includes(v.permission),
  ).map((v) => v.noun);
  const hasExtraVisibility = EXTRA_VISIBILITY_PERMISSIONS.some((p) =>
    permissions[role].includes(p),
  );

  if (nouns.length === 0 && !hasExtraVisibility) return null;

  const base =
    nouns.length > 0
      ? `Sees ${joinWithAnd(nouns)} detail on jobs`
      : 'Sees customer detail';
  const extra = hasExtraVisibility
    ? `${nouns.length > 0 ? ',' : ''} as well as on the job and user forms and the users table`
    : '';

  return `${base}${extra}.`;
};

/** One plain-language sentence describing who each role is for. */
export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  [UserRole.ADMIN]:
    "Manages your team's access and keeps jobs assigned to the right people.",
  [UserRole.HIRING_MANAGER]:
    'Reviews candidates and shares hiring feedback with the team.',
  [UserRole.RECRUITER]:
    'Runs day-to-day hiring: posting jobs and moving candidates through review.',
  [UserRole.SUPER_ADMIN]:
    "Avrio-internal role with visibility across every customer. Not assignable to a customer's own team.",
  [UserRole.HR_MANAGER]:
    'Records offers and sees candidate progress, without managing jobs or the team.',
  [UserRole.CHRO]:
    "HR Manager's scope, plus managing the team and the candidate-assessment view. Enterprise only.",
  [UserRole.CORPORATE_EXECUTIVE]:
    'Read-only oversight of candidates and hiring progress. Enterprise only.',
  [UserRole.INTERNAL_TECHNOLOGY]:
    'Manages team accounts and permissions. No visibility into candidates or jobs. Enterprise only.',
};

/**
 * Visual weight for the role tag/card, reusing statusPalette.ts's own
 * `active`/`idle` tones rather than a new palette: Admin and Super Admin
 * read with the same ink-on-transparent treatment already used for an
 * in-flight candidate status, Recruiter and Hiring Manager get the plainer
 * muted tone -- same restraint, applied to roles instead of statuses.
 */
export type RoleTagWeight = 'elevated' | 'plain';

export const ROLE_TAG_WEIGHT: Record<UserRole, RoleTagWeight> = {
  [UserRole.SUPER_ADMIN]: 'elevated',
  [UserRole.ADMIN]: 'elevated',
  [UserRole.HIRING_MANAGER]: 'plain',
  [UserRole.RECRUITER]: 'plain',
  // CHRO gets the same elevated weight as Admin/Super Admin - a senior,
  // team-managing role, same reasoning as the original two. The other three
  // new roles are plain, matching Hiring Manager/Recruiter's narrower scope.
  [UserRole.HR_MANAGER]: 'plain',
  [UserRole.CHRO]: 'elevated',
  [UserRole.CORPORATE_EXECUTIVE]: 'plain',
  [UserRole.INTERNAL_TECHNOLOGY]: 'plain',
};

/** Roles shown on the "Roles & permissions" tab, in display order. */
export const DISPLAYED_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.HIRING_MANAGER,
  UserRole.RECRUITER,
  UserRole.HR_MANAGER,
  UserRole.CHRO,
  UserRole.CORPORATE_EXECUTIVE,
  UserRole.INTERNAL_TECHNOLOGY,
  UserRole.SUPER_ADMIN,
];

export interface RoleOptionWithDescription {
  label: string;
  value: UserRole;
  description: string;
}

/**
 * The Add/Edit User modal's role choices, each carrying its one-line
 * description so a person assigning a role can read what it grants before
 * committing -- not just discover it afterward. Built on top of the
 * existing `roleOptions` (which already excludes Super Admin from the
 * assignable set) rather than a second list, so the two can't disagree
 * about which roles are assignable.
 */
export const ASSIGNABLE_ROLE_OPTIONS: RoleOptionWithDescription[] =
  roleOptions.map((option) => ({
    ...option,
    description: ROLE_DESCRIPTIONS[option.value],
  }));

/**
 * The reusable capability-group list, ahead of per-user permission overrides
 * (Phase 2). This module's role-card rendering (`roleCanDo`/`roleSeesLine`
 * above) is unchanged and reads exactly as it did -- this is a second,
 * additive view over the same underlying permissions, shaped for a toggle
 * list rather than a read-only summary: one row per independently-grantable
 * permission (so Phase 2 can flip any single one), each still carrying a
 * `group` so a toggle UI can section it the same way the role card does.
 *
 * `manageTeam` is the one row representing two permissions rather than one --
 * CAN_ADD_NEW_USER and CAN_SEE_USER_ACTIONS_COLUMN are granted identically to
 * every role today and read as a single capability ("manage my team") from a
 * user's perspective, not two. Every other row is a 1:1 mapping so a future
 * override can grant or revoke it independently.
 */
export type CapabilityGroupKey = 'canDo' | 'canSee';

export interface CapabilityGroupItem {
  key: string;
  group: CapabilityGroupKey;
  label: string;
  permissions: Permission[];
}

export const CAPABILITY_GROUPS: CapabilityGroupItem[] = [
  ...CAN_DO_ITEMS.map((item): CapabilityGroupItem => ({
    key: item.key,
    group: 'canDo',
    label: item.label,
    permissions:
      item.key === 'manageTeam'
        ? [item.permission, Permission.CAN_SEE_USER_ACTIONS_COLUMN]
        : [item.permission],
  })),
  {
    key: 'seeJobHiringManagerColumn',
    group: 'canSee',
    label: 'Hiring-manager column on jobs',
    permissions: [Permission.CAN_SEE_JOB_HIRING_MANAGER_COLUMN],
  },
  {
    key: 'seeJobRecruiterColumn',
    group: 'canSee',
    label: 'Recruiter column on jobs',
    permissions: [Permission.CAN_SEE_JOB_RECRUITER_COLUMN],
  },
  {
    key: 'seeJobCompanyColumn',
    group: 'canSee',
    label: 'Company column on jobs',
    permissions: [Permission.CAN_SEE_JOB_COMPANY_COLUMN],
  },
  {
    key: 'seeJobCustomerNameColumn',
    group: 'canSee',
    label: 'Customer column on jobs',
    permissions: [Permission.CAN_SEE_JOB_CUSTOMER_NAME_COLUMN],
  },
  {
    key: 'seeJobPostCustomerField',
    group: 'canSee',
    label: 'Customer field on the post-job form',
    permissions: [Permission.CAN_SEE_JOB_POST_CUSTOMER_FIELD],
  },
  {
    key: 'seeAddUserCustomerField',
    group: 'canSee',
    label: 'Customer field on the add-user form',
    permissions: [Permission.CAN_SEE_ADD_USER_CUSTOMER_FIELD],
  },
  {
    key: 'seeUserCustomerColumn',
    group: 'canSee',
    label: 'Customer column on the users table',
    permissions: [Permission.CAN_SEE_USER_CUSTOMER_COLUMN],
  },
];

export interface RoleCapabilityGroupResult extends CapabilityGroupItem {
  /** True only when every permission in the row is granted -- see the
   * manageTeam note above for why that's correct for today's data. */
  granted: boolean;
}

export const roleCapabilityGroups = (
  role: UserRole,
): RoleCapabilityGroupResult[] =>
  CAPABILITY_GROUPS.map((item) => ({
    ...item,
    granted: item.permissions.every((p) => permissions[role].includes(p)),
  }));
